import {useGoogleAuth} from '@features/settings/queries/useGoogleAuth'
import {Spinner} from '@components/ui/spinner'
import {useParsedSearchParams} from '@hooks/useParsedSearchParams'
import {z} from 'zod'
import {AlertTriangleIcon, CheckDone01Icon} from '@components/ui/icon'
import {useTranslation} from '@/translations/i18n'
import {ErrorBox} from '@components/commons/error-box'
import {Navigate} from 'react-router'
import {routes} from '@utilities/constants/routes'
import {ButtonLink} from '@components/commons/button-link'
import {useMe} from '@features/user/queries/useMe'
import {envVars} from '@/envVars'
import {useEffect} from 'react'
import {QUERY_KEYS} from '@/queryClient'
import {useQueryClient} from '@tanstack/react-query'

const ROOT_CLASSNAME = 'flex flex-col items-center justify-center gap-6 h-full w-full p-4 md:p-8 bg-white'
const SearchParams = z.object({
    code: z.string().optional(),
    state: z.string().optional()
})

const makeGoogleAuthUrl = (photographerId: number) => {
    const googleAuthUrl = new URL(envVars.VITE_APP_GOOGLE_OAUTH_URL)
    googleAuthUrl.searchParams.set('client_id', envVars.VITE_APP_GOOGLE_CLIENT_ID)
    googleAuthUrl.searchParams.set('response_type', 'code')
    googleAuthUrl.searchParams.set('redirect_uri', envVars.VITE_APP_GOOGLE_CALLBACK_URL)
    googleAuthUrl.searchParams.set('access_type', 'offline')
    googleAuthUrl.searchParams.set('prompt', 'consent')
    googleAuthUrl.searchParams.set(
        'scope',
        'https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile https://www.googleapis.com/auth/calendar https://www.googleapis.com/auth/calendar.events'
    )
    googleAuthUrl.searchParams.set('state', `${photographerId}`)
    return googleAuthUrl.toString()
}

export const GoogleAuthCallback = () => {
    const {t} = useTranslation()
    const {searchParams} = useParsedSearchParams(SearchParams)
    const queryClient = useQueryClient()

    const {isSuccess, isError, isPending} = useGoogleAuth({
        code: searchParams.code ?? '',
        state: searchParams.state ?? '',
        enabled: !!searchParams.code && !!searchParams.state
    })
    const userMeQuery = useMe({throwOnError: true})
    const user = userMeQuery.data

    useEffect(() => {
        if (isSuccess) {
            void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.ME]})
            void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.REQUIRED_ACTIONS]})
        }
    }, [isSuccess])

    if ((isPending && searchParams.code && searchParams.state) || userMeQuery.isPending)
        return (
            <div className={ROOT_CLASSNAME}>
                <Spinner size={40} />
            </div>
        )

    if (isError)
        return (
            <div className={ROOT_CLASSNAME}>
                <ErrorBox
                    title={t('errors:default')}
                    icon={<AlertTriangleIcon />}
                    cta={<ButtonLink to="/">{t('commons:go_home')}</ButtonLink>}
                />
            </div>
        )

    if (isSuccess) return <Navigate to={routes.SETTINGS.handle.fullPath} />

    return (
        <div className={ROOT_CLASSNAME}>
            <CheckDone01Icon width={48} height={48} />
            <div className="flex flex-col items-center justify-center gap-2 text-center">
                <h3 className="text-display-xs font-medium">
                    {t('settings:personal_information:google_redirect_title')}
                </h3>
                <p>{t('settings:personal_information:google_redirect_description')}</p>
            </div>
            {user && (
                <ButtonLink to={makeGoogleAuthUrl(user.id)}>
                    {t('settings:personal_information:google_redirect_action')}
                </ButtonLink>
            )}
        </div>
    )
}
